import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useNavigate } from "@tanstack/react-router";
import type { ComponentPropsWithoutRef } from "react";

interface NotificationMarkdownProps {
  content: string;
  inline?: boolean;
}

export function NotificationMarkdown({
  content,
  inline,
}: NotificationMarkdownProps) {
  const navigate = useNavigate();

  return (
    <Markdown
      remarkPlugins={[remarkGfm]}
      allowedElements={["p", "a", "strong", "em", "code", "del"]}
      unwrapDisallowed
      components={{
        p: ({ children }) =>
          inline ? (
            <span className="text-muted-foreground text-xs">{children}</span>
          ) : (
            <p className="text-muted-foreground text-sm leading-snug">
              {children}
            </p>
          ),
        a: ({ href, children }: ComponentPropsWithoutRef<"a">) => {
          const isInternal = href?.startsWith("/");
          return (
            <a
              href={href}
              className="text-rose-500 hover:underline"
              onClick={(e) => {
                if (isInternal && href) {
                  e.preventDefault();
                  e.stopPropagation();
                  void navigate({ to: href });
                }
              }}
              {...(!isInternal && {
                target: "_blank",
                rel: "noopener noreferrer",
              })}
            >
              {children}
            </a>
          );
        },
        strong: ({ children }) => (
          <strong className="text-foreground font-semibold">{children}</strong>
        ),
        code: ({ children }) => (
          <code className="bg-muted rounded px-1 py-0.5 font-mono text-xs">
            {children}
          </code>
        ),
      }}
    >
      {content}
    </Markdown>
  );
}
